import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { AlertTriangle, CheckCircle2, ClipboardCheck, Loader2, Lock, Plus, RefreshCw, Trash2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface ReviewLine {
  id: number;
  reference: string;
  amount: number;
  description: string;
  matched: boolean;
  approved: boolean;
  approval?: { userId: number; approvedAt: string; reason: string } | null;
}
interface ReviewBankPayment {
  id: number;
  paymentDate: string;
  amount: number;
  narrative: string;
}
interface ReviewData {
  account: string | null;
  bankPayments: ReviewBankPayment[];
  lines: ReviewLine[];
  coveragePercent: number;
  parsedTotal: number;
  statedTotal: number | null;
  locked: boolean;
}
interface AddedRowDraft {
  key: number;
  reference: string;
  amount: string;
  description: string;
}
interface ApproveResult {
  ok: true;
  bankPaymentId: number;
  remittanceId: number;
  total: number;
  approvedCount: number;
}

const toPence = (n: number) => Math.round(n * 100);
function fmtSigned(n: number) {
  const s = Math.abs(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? "-" : ""}£${s}`;
}
function fmtDay(iso: string) {
  try { return format(parseISO(iso), "d MMM yyyy"); } catch { return iso; }
}
// Strict signed money: optional minus, digits, up to 2dp. No autofill.
function parseAmount(v: string): number | null {
  const t = v.trim().replace(/[£,\s]/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

export function RemittanceUnallocatedReview({ remittanceId, locked }: { remittanceId: number; locked: boolean }) {
  const queryClient = useQueryClient();
  const queryKey = ["cash-receipts", "remittance-unallocated-review", remittanceId];
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittanceId}/unallocated-review`, { credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as ReviewData;
    },
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [added, setAdded] = useState<AddedRowDraft[]>([]);
  const [nextKey, setNextKey] = useState(1);
  const [bankPaymentId, setBankPaymentId] = useState<number | null>(null);
  const [account, setAccount] = useState("");
  const [accountConfirmed, setAccountConfirmed] = useState(false);
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [success, setSuccess] = useState<ApproveResult | null>(null);

  const unresolved = useMemo(() => (data?.lines ?? []).filter((l) => !l.matched && !l.approved), [data]);
  const effectiveAccount = account.trim() || data?.account || "";

  const parsedAdded = added.map((r) => ({ ...r, value: parseAmount(r.amount) }));
  const addedValid = parsedAdded.every((r) => r.reference.trim() && r.description.trim() && r.value !== null);
  const addedNetP = parsedAdded.reduce((s, r) => s + (r.value !== null ? toPence(r.value) : 0), 0);
  const parsedTotalP = data ? toPence(data.parsedTotal) : 0;
  const bank = data?.bankPayments.find((b) => b.id === bankPaymentId) ?? null;
  const bankP = bank ? toPence(bank.amount) : null;
  const differenceP = bankP === null ? null : bankP - (parsedTotalP + addedNetP);
  const reconciled = differenceP === 0;
  const allSelected = unresolved.length > 0 ? unresolved.every((l) => selected.has(l.id)) : true;
  const absoluteTotalP = (data?.lines ?? []).reduce((s, l) => s + Math.abs(toPence(l.amount)), 0)
    + parsedAdded.reduce((s, r) => s + (r.value == null ? 0 : Math.abs(toPence(r.value))), 0);
  const matchedValueP = (data?.lines ?? []).filter((l) => l.matched)
    .reduce((s, l) => s + Math.abs(toPence(l.amount)), 0);
  const coveragePercent = absoluteTotalP > 0 ? matchedValueP / absoluteTotalP * 100 : 0;
  const coverageOk = coveragePercent > 90;
  const isLocked = locked || !!data?.locked;
  const hasWork = unresolved.length > 0 || added.length > 0;

  const blockers: string[] = [];
  if (isLocked) blockers.push("Posting has started; this remittance is locked.");
  if (!coverageOk) blockers.push("Value coverage must exceed 90%.");
  if (!hasWork) blockers.push("No unresolved rows and no added rows.");
  if (!allSelected) blockers.push("Every unresolved row must be selected.");
  if (!addedValid) blockers.push("Each added row needs a reference, description and non-zero signed amount.");
  if (!bank) blockers.push("Select the bank payment.");
  if (!effectiveAccount || !accountConfirmed) blockers.push("Confirm the customer account.");
  if (bank && !reconciled) blockers.push("Bank amount must reconcile to the penny.");
  if (reason.trim().length < 5) blockers.push("An approval reason of at least five characters is required.");
  if (!ack) blockers.push("Acknowledge that approval does not post to Merlin.");

  const approve = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${BASE}/api/cash-receipts/remittances/${remittanceId}/unallocated-review`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Requested-With": "fetch" },
        body: JSON.stringify({
          bankPaymentId,
          account: effectiveAccount.toUpperCase(),
          reason: reason.trim(),
          lineIds: [...selected].filter((id) => unresolved.some((l) => l.id === id)),
          addedRows: parsedAdded.map((r) => ({ reference: r.reference.trim(), amount: r.value as number, description: r.description.trim() })),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      return body as ApproveResult;
    },
    onSuccess: (r) => {
      setSuccess(r);
      setSelected(new Set());
      setAdded([]);
      setBankPaymentId(null);
      setAccount("");
      setAccountConfirmed(false);
      setReason("");
      setAck(false);
      queryClient.invalidateQueries({ queryKey: ["cash-receipts"] });
    },
  });

  const canSubmit = blockers.length === 0 && !approve.isPending;

  if (isLoading) {
    return (
      <div className="mb-3 rounded-lg border border-slate-200 p-3 space-y-2 animate-pulse" data-testid="unallocated-review-loading">
        <div className="h-3 w-48 bg-slate-200 rounded" />
        <div className="h-3 w-full bg-slate-100 rounded" />
        <div className="h-3 w-2/3 bg-slate-100 rounded" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 flex items-center justify-between">
        <span>Couldn't load unallocated review: {(error as Error)?.message ?? "unknown error"}</span>
        <button type="button" onClick={() => refetch()} className="underline" data-testid="button-retry-unallocated-review">Retry</button>
      </div>
    );
  }
  // Keep the entry point available for source rows the parser missed entirely.

  const toggle = (id: number) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const updateAdded = (key: number, patch: Partial<AddedRowDraft>) => setAdded((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const inputCls = "text-xs px-1.5 py-1 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50";

  return (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs" data-testid="section-unallocated-review" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-semibold text-amber-900">
          <ClipboardCheck className="w-4 h-4" />
          Review unmatched rows as unallocated
          {isLocked && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-normal text-slate-500"><Lock className="w-2.5 h-2.5" />locked</span>}
        </div>
        <button type="button" onClick={() => refetch()} className="text-slate-400 hover:text-primary" title="Refresh" data-testid="button-refresh-unallocated-review">
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} />
        </button>
      </div>

      {success && (
        <div className="mb-2 rounded border border-emerald-300 bg-emerald-50 p-2 text-emerald-800 flex items-start gap-1.5" data-testid="status-unallocated-approved">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <div>
            Approved {success.approvedCount} row{success.approvedCount === 1 ? "" : "s"} in an allocation totalling <span className="font-mono">{fmtSigned(success.total)}</span>, linked to bank payment #{success.bankPaymentId}.
            Nothing has been posted to Merlin; post the bank payment through the normal flow.
          </div>
        </div>
      )}

      {data.lines.some((l) => l.approved) && (
        <div className="mb-3 rounded border border-emerald-200 bg-emerald-50 p-2">
          <p className="font-semibold">Approved for 999999</p>
          {data.lines.filter((l) => l.approved).map((l) => (
            <div key={l.id} className="mt-1">
              {l.reference} · {fmtSigned(l.amount)} · user #{l.approval?.userId} · {l.approval?.approvedAt && fmtDay(l.approval.approvedAt)}
              <div className="text-slate-600">{l.approval?.reason}</div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
        {[
          ["Coverage", `${coveragePercent.toFixed(2)}%`, coverageOk ? "text-emerald-700" : "text-red-700"],
          ["Parsed total", fmtSigned(data.parsedTotal), ""],
          ["Added net", fmtSigned(addedNetP / 100), ""],
          ["Bank amount", bank ? fmtSigned(bank.amount) : "—", ""],
          ["Difference", differenceP === null ? "—" : fmtSigned(differenceP / 100), differenceP === null ? "" : reconciled ? "text-emerald-700" : "text-red-700"],
        ].map(([label, value, cls]) => (
          <div key={label} className="rounded bg-white border border-slate-200 px-2 py-1">
            <div className="text-[10px] uppercase text-slate-400">{label}</div>
            <div className={`font-mono font-semibold ${cls}`} data-testid={`text-review-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</div>
          </div>
        ))}
      </div>
      {data.statedTotal !== null && <div className="mb-2 text-[11px] text-slate-500">Stated remittance total: <span className="font-mono">{fmtSigned(data.statedTotal)}</span></div>}

      {unresolved.length > 0 && (
        <table className="w-full bg-white rounded border border-slate-200 mb-2">
          <thead className="text-[10px] uppercase text-slate-400">
            <tr>
              <th className="px-2 py-1 w-6">
                <input type="checkbox" disabled={isLocked} checked={allSelected} onChange={(e) => setSelected(e.target.checked ? new Set(unresolved.map((l) => l.id)) : new Set())} data-testid="checkbox-select-all-unresolved" />
              </th>
              <th className="px-2 py-1 text-left">Reference</th>
              <th className="px-2 py-1 text-left">Description</th>
              <th className="px-2 py-1 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {unresolved.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="px-2 py-1"><input type="checkbox" disabled={isLocked} checked={selected.has(l.id)} onChange={() => toggle(l.id)} data-testid={`checkbox-unresolved-${l.id}`} /></td>
                <td className="px-2 py-1 font-mono">{l.reference}</td>
                <td className="px-2 py-1 text-slate-600">{l.description || "—"}</td>
                <td className={`px-2 py-1 text-right font-mono ${l.amount < 0 ? "text-red-700" : ""}`}>{fmtSigned(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="font-medium text-slate-700">Missing document rows</span>
          <button type="button" disabled={isLocked} onClick={() => { setAdded((r) => [...r, { key: nextKey, reference: "", amount: "", description: "" }]); setNextKey((k) => k + 1); }} className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50" data-testid="button-add-missing-row">
            <Plus className="w-3 h-3" />Add row
          </button>
        </div>
        <p className="text-[11px] text-slate-500 mb-1">Only for rows genuinely on the source document but missing from the parse. Enter values exactly as printed, with sign.</p>
        {added.map((r) => {
          const bad = r.amount.trim() !== "" && parseAmount(r.amount) === null;
          return (
            <div key={r.key} className="flex flex-wrap gap-1 mb-1 items-center">
              <input className={`${inputCls} font-mono w-32`} placeholder="Original reference" value={r.reference} disabled={isLocked} onChange={(e) => updateAdded(r.key, { reference: e.target.value })} data-testid={`input-added-reference-${r.key}`} />
              <input className={`${inputCls} font-mono w-28 text-right ${bad ? "border-red-400" : ""}`} placeholder="-12.34" value={r.amount} disabled={isLocked} onChange={(e) => updateAdded(r.key, { amount: e.target.value })} data-testid={`input-added-amount-${r.key}`} />
              <input className={`${inputCls} flex-1 min-w-[10rem]`} placeholder="Source description" value={r.description} disabled={isLocked} onChange={(e) => updateAdded(r.key, { description: e.target.value })} data-testid={`input-added-description-${r.key}`} />
              <button type="button" onClick={() => setAdded((rows) => rows.filter((x) => x.key !== r.key))} className="text-slate-400 hover:text-red-600" title="Remove draft row" data-testid={`button-remove-added-${r.key}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-2 mb-2">
        <label className="block">
          <span className="text-slate-600">Bank payment</span>
          <select className={`${inputCls} w-full mt-0.5`} disabled={isLocked} value={bankPaymentId ?? ""} onChange={(e) => setBankPaymentId(e.target.value ? Number(e.target.value) : null)} data-testid="select-bank-payment">
            <option value="">Select payment…</option>
            {data.bankPayments.map((b) => (
              <option key={b.id} value={b.id}>{fmtDay(b.paymentDate)} · {fmtSigned(b.amount)} · {b.narrative}</option>
            ))}
          </select>
          {data.bankPayments.length === 0 && <span className="text-[11px] text-red-600">No candidate bank payments.</span>}
        </label>
        <label className="block">
          <span className="text-slate-600">Customer account</span>
          <div className="flex items-center gap-2 mt-0.5">
            <input className={`${inputCls} font-mono w-28 uppercase`} placeholder={data.account ?? "e.g. HGL001"} value={account} disabled={isLocked} onChange={(e) => { setAccount(e.target.value); setAccountConfirmed(false); }} data-testid="input-review-account" />
            <label className="inline-flex items-center gap-1 text-slate-600">
              <input type="checkbox" disabled={isLocked || !effectiveAccount} checked={accountConfirmed} onChange={(e) => setAccountConfirmed(e.target.checked)} data-testid="checkbox-confirm-account" />
              Confirm <span className="font-mono">{effectiveAccount.toUpperCase() || "—"}</span>
            </label>
          </div>
        </label>
      </div>

      <label className="block mb-2">
        <span className="text-slate-600">Approval reason (required)</span>
        <textarea className={`${inputCls} w-full mt-0.5`} rows={2} value={reason} disabled={isLocked} onChange={(e) => setReason(e.target.value)} data-testid="input-approval-reason" />
      </label>

      <label className="flex items-start gap-1.5 mb-2 rounded border border-amber-300 bg-amber-100/60 p-2 text-amber-900">
        <input type="checkbox" className="mt-0.5" disabled={isLocked} checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="checkbox-ack-no-merlin" />
        <span>I understand approval links this allocation to the bank payment but does <strong>not</strong> post to Merlin. The bank payment is posted afterwards through the normal flow.</span>
      </label>

      {blockers.length > 0 && (
        <ul className="mb-2 space-y-0.5 text-[11px] text-slate-500" data-testid="list-review-blockers">
          {blockers.map((b) => <li key={b} className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-amber-500" />{b}</li>)}
        </ul>
      )}
      {approve.isError && <div className="mb-2 text-red-700" data-testid="text-review-error">{(approve.error as Error).message}</div>}

      <button type="button" disabled={!canSubmit} onClick={() => approve.mutate()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50" data-testid="button-approve-unallocated">
        {approve.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        Approve as unallocated
      </button>
    </div>
  );
}
